import { fnv1a, fnv1aHex, stableStringify } from '@/utils/hash';

/**
 * === Capa de obfuscación ===
 *
 * Objetivo: fricción anti-cheat casual. NO es criptografía.
 * Todo el código del obfuscador está en el bundle compilado, así que
 * un atacante dedicado puede siempre romperlo. El target es el jugador
 * casual que abre DevTools, ve `health: 100` y lo edita a `9999`.
 *
 * Mecanismos:
 * - Clave de sesión (offset) generado en primer arranque, persistido obfuscado.
 * - Valores numéricos: offset → XOR → *prime → base36.
 * - Blobs JSON: serialización estable + checksum FNV-1a.
 */

const BASE_SECRET = 'capybara-keeper-friction-v1'; // se mezcla en el offset

function rotL32(value: number, bits: number): number {
  const b = bits & 31;
  return ((value << b) | (value >>> (32 - b))) >>> 0;
}

function rotR32(value: number, bits: number): number {
  const b = bits & 31;
  return ((value >>> b) | (value << (32 - b))) >>> 0;
}

/** Genera un offset de sesión pseudo-aleatorio para esta instalación. */
export function generarOffsetSesion(): number {
  const random = Math.floor(Math.random() * 0xffffffff);
  const mezcla = fnv1a(`${BASE_SECRET}/${random}/${Date.now()}`);
  return (random ^ mezcla) >>> 0;
}

/**
 * Transforma un número en una representación opaca string.
 * Reversible con `desobfuscarNumero` usando el mismo offset.
 *
 * Estrategia: normalizar a int32 (con factor x1000 para preservar decimales),
 * XOR con offset, rotar bits y codificar en base36.
 */
export function obfuscarNumero(valor: number, offset: number): string {
  const asInt = Math.round(valor * 1000) | 0;
  const xored = (asInt ^ offset) >>> 0;
  const rotado = rotL32(xored, 13);
  return rotado.toString(36);
}

/** Revierte `obfuscarNumero`. */
export function desobfuscarNumero(codificado: string, offset: number): number {
  const rotado = parseInt(codificado, 36) >>> 0;
  const xored = rotR32(rotado, 13);
  const asInt = (xored ^ offset) | 0;
  return asInt / 1000;
}

/** Blob obfuscado que se persiste en localStorage. */
export interface BlobObfuscado {
  /** Versión del schema de obfuscación (no del data). */
  ov: number;
  /** Payload JSON serializado (ya transformado). */
  p: string;
  /** Checksum FNV-1a del payload + clave. */
  c: string;
}

const OBFUSCATION_VERSION = 1;

/**
 * Transforma el payload JSON a bytes UTF-8, les aplica un XOR rotatorio
 * derivado del offset y los codifica a base64. Ilegible a ojo desnudo.
 * Pasar por UTF-8 garantiza que tildes y ñ no rompan `btoa`.
 */
function transformarPayload(texto: string, offset: number): string {
  const utf8 = new TextEncoder().encode(texto);
  const xored = new Uint8Array(utf8.length);
  for (let i = 0; i < utf8.length; i++) {
    const rotated = (offset + i * 31) & 0xff;
    xored[i] = utf8[i] ^ rotated;
  }
  let binary = '';
  for (let i = 0; i < xored.length; i++) binary += String.fromCharCode(xored[i]);
  return btoa(binary);
}

function revertirPayload(codificado: string, offset: number): string {
  const binary = atob(codificado);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    const rotated = (offset + i * 31) & 0xff;
    bytes[i] = binary.charCodeAt(i) ^ rotated;
  }
  return new TextDecoder().decode(bytes);
}

/** Serializa un objeto a blob obfuscado con checksum. */
export function empaquetar<T>(valor: T, offset: number, claveOpaca: string): BlobObfuscado {
  const serializado = stableStringify(valor);
  const payloadTransformado = transformarPayload(serializado, offset);
  const checksum = fnv1aHex(`${serializado}/${claveOpaca}/${offset}`);
  return {
    ov: OBFUSCATION_VERSION,
    p: payloadTransformado,
    c: checksum,
  };
}

export type DesempaquetarResultado<T> =
  | { ok: true; valor: T }
  | { ok: false; razon: 'formato'; mensaje: string }
  | { ok: false; razon: 'version'; mensaje: string }
  | { ok: false; razon: 'tampered'; mensaje: string };

/**
 * Revierte un blob. Distingue entre:
 * - éxito,
 * - data corrupta/formato incorrecto (no culpa del jugador),
 * - versión incompatible (migrar o nuevo juego),
 * - **tampered** (checksum falla sobre data bien formada → dispara cosmic game over).
 */
export function desempaquetar<T>(
  blob: unknown,
  offset: number,
  claveOpaca: string,
): DesempaquetarResultado<T> {
  if (!blob || typeof blob !== 'object') {
    return { ok: false, razon: 'formato', mensaje: 'Blob inexistente o no es objeto.' };
  }
  const b = blob as Partial<BlobObfuscado>;
  if (typeof b.ov !== 'number' || typeof b.p !== 'string' || typeof b.c !== 'string') {
    return { ok: false, razon: 'formato', mensaje: 'Blob no tiene los campos esperados.' };
  }
  if (b.ov !== OBFUSCATION_VERSION) {
    return {
      ok: false,
      razon: 'version',
      mensaje: `Schema de obfuscación v${b.ov}, se esperaba v${OBFUSCATION_VERSION}.`,
    };
  }

  let serializado: string;
  try {
    serializado = revertirPayload(b.p, offset);
  } catch {
    return { ok: false, razon: 'formato', mensaje: 'No se pudo desencodear el payload.' };
  }

  let valor: T;
  try {
    valor = JSON.parse(serializado) as T;
  } catch {
    return { ok: false, razon: 'formato', mensaje: 'Payload desencodeado no es JSON válido.' };
  }

  // Chequeo de checksum sobre data bien formada → si falla, es tampering.
  const checksumEsperado = fnv1aHex(`${serializado}/${claveOpaca}/${offset}`);
  if (checksumEsperado !== b.c) {
    return {
      ok: false,
      razon: 'tampered',
      mensaje: `Checksum mismatch: esperado ${checksumEsperado}, obtenido ${b.c}.`,
    };
  }

  return { ok: true, valor };
}
